import {
  FormControlLabel,
  GKButton,
  GKTextField,
  Grid2,
  Switch,
  Typography,
} from "components";
import { CompanySettingApi } from "interfaces/Settings";
import { Controller, useForm } from "react-hook-form";
import { useUpdateCompanySettingsMutation } from "store/features/CompanySetting";
import useDocumentTitle from "utils/useDocumentTitle";

interface Props {
  data: CompanySettingApi;
  companyId: string;
}

const EquityBasketSetting = (props: Props) => {
  useDocumentTitle("Basket");

  const { data, companyId } = props;

  const [updateCompanySettings, { isLoading }] =
    useUpdateCompanySettingsMutation();

  const form = useForm({
    defaultValues: {
      orderPriceRangePercentage: 0,
      capToLedgerBalance: false,
      useBrokerBalance: false,
      useCeAmount: false,
      addToTradBook: false,
      separateBuySellTransactions: false,
    },
    values: {
      orderPriceRangePercentage: data?.orderPriceRangePercentage,
      capToLedgerBalance: data?.capToLedgerBalance,
      useBrokerBalance: data?.useBrokerBalance,
      useCeAmount: data?.useCeAmount,
      addToTradBook: data?.addToTradBook,
      separateBuySellTransactions: data?.separateBuySellTransactions,
    },
  });

  return (
    <form
      id="equity-basket-setting-form"
      onSubmit={form.handleSubmit((values) =>
        updateCompanySettings({
          id: companyId,
          body: values,
        }),
      )}
    >
      <Grid2 container spacing={2}>
        <Grid2 size={12} display={"flex"} alignItems={"center"} gap={1}>
          <Controller
            name="capToLedgerBalance"
            control={form.control}
            render={({ field }) => (
              <FormControlLabel
                control={
                  <Switch
                    {...field}
                    checked={field.value}
                    onChange={(e) => {
                      field.onChange(e.target.checked);
                    }}
                  />
                }
                label="Limit allocation amount to ledger balance"
              />
            )}
          />
        </Grid2>
        <Grid2 size={12} display={"flex"} alignItems={"center"} gap={1}>
          <Controller
            name="useBrokerBalance"
            control={form.control}
            render={({ field }) => (
              <FormControlLabel
                control={
                  <Switch
                    {...field}
                    checked={field.value}
                    onChange={(e) => {
                      field.onChange(e.target.checked);
                    }}
                  />
                }
                label="Use available margin for allocation/rebalance/exit"
              />
            )}
          />
        </Grid2>
        <Grid2 size={12}>
          <Controller
            name="useCeAmount"
            control={form.control}
            render={({ field }) => {
              return (
                <FormControlLabel
                  control={
                    <Switch
                      {...field}
                      checked={field.value}
                      onChange={(e) => {
                        field.onChange(e.target.checked);
                      }}
                    />
                  }
                  label="Use cash equivalents as cash while allocation"
                />
              );
            }}
          />
        </Grid2>
        <Grid2 size={12}>
          <Controller
            name="addToTradBook"
            control={form.control}
            render={({ field }) => {
              return (
                <FormControlLabel
                  control={
                    <Switch
                      {...field}
                      checked={field.value}
                      onChange={(e) => {
                        field.onChange(e.target.checked);
                      }}
                    />
                  }
                  label="Enable addition of recommended orders to the tradebook."
                />
              );
            }}
          />
        </Grid2>
        <Grid2 size={12}>
          <Controller
            name="separateBuySellTransactions"
            control={form.control}
            render={({ field }) => {
              return (
                <FormControlLabel
                  control={
                    <Switch
                      {...field}
                      checked={field.value}
                      onChange={(e) => {
                        field.onChange(e.target.checked);
                      }}
                    />
                  }
                  label="Split buy and sell trades into separate emails for execution"
                />
              );
            }}
          />
        </Grid2>
        <Grid2 size={12} display={"flex"} alignItems={"center"} gap={2}>
          <Typography width={"70%"}>
            Price range percentage for sending orders to clients
          </Typography>

          <Controller
            name="orderPriceRangePercentage"
            control={form.control}
            render={({ field }) => (
              <GKTextField
                {...field}
                sx={{ width: "100px" }}
                size="small"
                type="number"
                onChange={(e: any) => field.onChange(e.target.valueAsNumber)}
                slotProps={{ input: { endAdornment: "%" } }}
              />
            )}
          />
        </Grid2>
        <Grid2 size={12} display={"flex"} justifyContent={"flex-end"}>
          <GKButton variant="contained" type="submit" loading={isLoading}>
            Save
          </GKButton>
        </Grid2>
      </Grid2>
    </form>
  );
};

export default EquityBasketSetting;
